/**
 * DocumentStatusGuide.tsx
 * 書類ステータスガイドバナー。
 * 下書き（黄色）と発行済み（緑/白）のバナーを表示する。
 * ロジックは DocumentPage に残し、表示のみ担当する。
 */
import React from "react";
import { btnStyle } from "../../App.js";
import type { DocumentTemplate } from "../../api.js";

interface DocumentStatusGuideProps {
  isDraft: boolean;
  isIssued: boolean;
  /** 見積書は発行後も「内容を変更する」で変更可能（新しい版を自動作成） */
  isEstimate?: boolean;
  justIssued: boolean;
  editing: boolean;
  templateForDoc: DocumentTemplate | undefined;
  /** 「見本に合わせた〇〇の体裁」の〇〇。書類種別ごとにプリセットが異なる */
  docTypeLabel: string;
  presetAvailable: boolean;
  showPresetButton: boolean;
  createTemplatePending: boolean;
  installPresetPending: boolean;
  pdfLoading: boolean;
  onCreateTemplate: () => void;
  onInstallPreset: () => void;
  onDownloadPdf: () => void;
}

export function DocumentStatusGuide({
  isDraft, isIssued, isEstimate, justIssued, editing,
  templateForDoc, docTypeLabel, presetAvailable, showPresetButton,
  createTemplatePending, installPresetPending, pdfLoading,
  onCreateTemplate, onInstallPreset, onDownloadPdf,
}: DocumentStatusGuideProps) {
  if (isDraft) {
    return (
      <section style={{
        background: "#fffbeb",
        border: "1px solid #fcd34d",
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: "#92400e" }}>
              下書きです（まだ発行されていません）
            </div>
            <div style={{ fontSize: 12, color: "#92400e", marginTop: 3 }}>
              {editing
                ? "内容を編集して右上の「PDFを発行」を押すと発行・プレビューできます。"
                : "「再編集」で内容を修正できます。発行する場合は再編集後に「PDFを発行」を押してください。"}
            </div>
          </div>
          {!editing && !templateForDoc && !presetAvailable && (
            <div style={{ flexShrink: 0 }}>
              <button
                style={btnStyle("outline")}
                onClick={onCreateTemplate}
                disabled={createTemplatePending}
              >
                {createTemplatePending ? "準備中…" : "体裁を準備する"}
              </button>
            </div>
          )}
        </div>
        {showPresetButton && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#fff7ed", border: "1px solid #fed7aa",
            borderRadius: 8, padding: "10px 14px", gap: 12,
          }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>
              <strong>汎用レイアウトが適用されています。</strong>
              　見本に合わせた「{docTypeLabel}」の体裁を適用できます。
            </div>
            <button
              style={{ ...btnStyle("outline"), flexShrink: 0, whiteSpace: "nowrap" }}
              onClick={onInstallPreset}
              disabled={installPresetPending}
            >
              {installPresetPending ? "適用中…" : "見本の体裁を適用"}
            </button>
          </div>
        )}
      </section>
    );
  }

  if (isIssued) {
    return (
      <section style={{
        background: justIssued ? "#f0fdfa" : "#fff",
        border: `1px solid ${justIssued ? "#99f6e4" : "#e2e8f0"}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
            {justIssued ? "発行が完了しました" : "発行済み書類"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
            {isEstimate
              ? "PDFをダウンロードして保存・送付できます。内容は「内容を変更する」からいつでも変更できます（新しい版として保存されます）。"
              : "PDFをダウンロードして保存・送付できます。必要ならこの画面から修正版の作成もできます。"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button style={btnStyle("primary")} onClick={onDownloadPdf} disabled={pdfLoading}>
            {pdfLoading ? "生成中…" : "PDFをダウンロード"}
          </button>
        </div>
      </section>
    );
  }

  return null;
}
